<template>
  <!-- Datadog would otherwise name clicks after their text, which here holds doctors' names and addresses -->
  <ResponsiveLayout data-dd-action-name="Action link outreach page">
    <!-- The steps follow the app's outreach screens: the practice typed in or searched, then the
         personal details with the consent -->
    <template
      v-if="backTarget"
      #header_start
    >
      <ButtonBack
        :disabled="state === STATES.SUBMITTING"
        data-qa="back"
        data-dd-action-name="Back"
        @click="goBack"
      />
    </template>
    <template #header_end><HeaderHelp /></template>

    <div
      class="kds-main-wrapper"
      data-qa="request-prescription-link"
    >
      <div
        v-if="state === STATES.CHECKING"
        class="request-prescription-link__spinner"
        data-qa="checking"
      >
        <Base64Spinner />
      </div>

      <div
        v-else-if="state === STATES.INVALID"
        class="kds-content-block"
        data-qa="invalid"
      >
        <p class="kds-typography-title-xlarge">
          {{ $t('eu__upload_prescription_link__invalid__header') }}
        </p>
        <!-- Catalogue copy with the support links (email, WhatsApp, phone) -->
        <p
          class="kds-typography-paragraph-large"
          v-html="$t('eu__upload_prescription_link__invalid__description')"
        />
      </div>

      <div
        v-else-if="state === STATES.UNAVAILABLE"
        class="kds-content-block"
        data-qa="unavailable"
      >
        <p class="kds-typography-title-xlarge">
          {{ $t('eu__upload_prescription_link__unavailable__header') }}
        </p>
        <p class="kds-typography-paragraph-large">
          {{ $t('eu__upload_prescription_link__unavailable__description') }}
        </p>
      </div>

      <div
        v-else-if="state === STATES.DONE"
        class="kds-content-block"
        data-qa="done"
      >
        <p class="kds-typography-title-xlarge">
          {{ $t('eu__request_prescription_link__done__header') }}
        </p>
        <p class="kds-typography-paragraph-large">
          {{ $t('eu__request_prescription_link__done__description') }}
        </p>
      </div>

      <div
        v-else-if="state === STATES.ADD_PRESCRIBER"
        class="kds-main-wrapper"
        data-qa="add-prescriber"
      >
        <div class="kds-content-block">
          <p class="kds-typography-title-xlarge">
            {{ $t('eu__doctor_outreach_add_prescriber__title') }}
          </p>
          <p class="kds-typography-paragraph-large">
            {{ $t('eu__doctor_outreach_add_prescriber__description') }}
          </p>
        </div>
        <form
          id="request-prescription-add-prescriber"
          class="kds-content-block"
          autocomplete="off"
          @submit.prevent
        >
          <!-- useForm keeps a form's fields disabled once it was submitted,
               and Back must leave the typed practice editable -->
          <TextField
            :label="$t('eu__prescriber_information__prescriber_name')"
            data-qa="prescriber-name"
            v-bind="manualEntryAttrs.prescriberName"
            :disabled="false"
          />
          <TextField
            :label="$t('eu__prescriber_information__city_or_zip')"
            data-qa="city-or-zip"
            v-bind="manualEntryAttrs.cityOrZip"
            :disabled="false"
          />
        </form>
      </div>

      <div
        v-else-if="state === STATES.PRESCRIBER_SEARCH"
        class="kds-main-wrapper"
        data-qa="prescriber-search"
      >
        <div class="kds-content-block">
          <p class="kds-typography-title-xlarge">
            {{ $t('eu__doctor_outreach_prescriber_search__title') }}
          </p>
          <p class="kds-typography-paragraph-large">
            {{ $t('eu__doctor_outreach_prescriber_search__description') }}
          </p>
        </div>
        <form
          id="request-prescription-search"
          class="kds-content-block"
          autocomplete="off"
          @submit.prevent
        >
          <!-- The backend leaves words shorter than 3 characters out of a search, and answers a
               name over 64 characters with a 400 that the search dialog would offer to retry -->
          <DoctorPracticePicker
            ref="pickerRef"
            v-model:query="practiceQuery"
            v-model:practice="pickedPractice"
            :confirmed-practice-id="confirmedPracticeId"
            data-qa="doctor-practice-search-field"
            :label="$t('eu__doctor_outreach_prescriber_search__label')"
            :min-query-length="3"
            maxlength="64"
            option-action-name="Practice search result"
            manual-entry-action-name="Choose practice manually"
            :search-fn="findPractices"
            :featured-fn="findNearestPractices"
            :search-error-title="$t('generic__error_title')"
            :search-error-description="$t('generic__try_again')"
            @confirm="confirmedPracticeId = $event.id"
            @manual-entry="state = STATES.ADD_PRESCRIBER"
          />
        </form>
      </div>

      <div
        v-else-if="onPersonalStep"
        class="kds-main-wrapper"
        :data-qa="state"
      >
        <div class="kds-content-block">
          <p class="kds-typography-title-xlarge">
            {{ $t('eu__doctor_outreach_personal_information__title') }}
          </p>
          <p class="kds-typography-paragraph-large">
            {{ $t('eu__doctor_outreach_personal_information__description') }}
          </p>
        </div>
        <form
          id="request-prescription-personal"
          class="kds-content-block"
          autocomplete="off"
          @submit.prevent
        >
          <TextField
            :label="$t('eu__doctor_outreach_personal_information__first_name')"
            data-qa="first-name"
            autocomplete="given-name"
            v-bind="personalDetailsAttrs.firstName"
          />
          <TextField
            :label="$t('eu__doctor_outreach_personal_information__last_name')"
            data-qa="last-name"
            :hint="
              $t('eu__doctor_outreach_personal_information__last_name__hint')
            "
            autocomplete="family-name"
            v-bind="personalDetailsAttrs.lastName"
          />
          <DateOfBirthField
            lang="de-DE"
            v-bind="personalDetailsAttrs.dateOfBirth"
          />
          <BaseBanner variant="neutral">
            <DocumentViewer
              class="kds-typography-paragraph-medium"
              :link="consentText"
            />
          </BaseBanner>
        </form>

        <BaseModal
          ref="requestErrorModalRef"
          :title="$t('generic__error_title')"
        >
          <p
            class="kds-typography-paragraph-large"
            v-html="
              $t('generic__error_message', { email: env.VITE_SUPPORT_EMAIL })
            "
          />
          <template #footer>
            <div class="request-prescription-link__modal-actions">
              <BaseButton
                size="small"
                variant="outlined"
                data-qa="prescription-consent-error-cancel"
                data-dd-action-name="Close outreach error"
                @click="closeRequestError"
              >
                {{
                  $t('generic__third_party_url_confirmation__secondary_label')
                }}
              </BaseButton>
              <BaseButton
                size="small"
                data-qa="prescription-consent-error-retry"
                data-dd-action-name="Retry outreach request"
                @click="retryRequest"
              >
                {{ $t('generic__try_again') }}
              </BaseButton>
            </div>
          </template>
        </BaseModal>
      </div>
    </div>

    <template
      v-if="state === STATES.ADD_PRESCRIBER"
      #actions
    >
      <BaseButton
        :text="$t('generic_action_continue')"
        type="submit"
        form="request-prescription-add-prescriber"
        data-qa="continue"
        data-dd-action-name="Continue to personal details"
        @click="submitManualEntry"
      />
      <BaseButton
        :text="$t('eu__doctor_outreach_add_prescriber__action_doctor_search')"
        variant="outlined"
        data-qa="doctor-search"
        data-dd-action-name="Search practice instead"
        @click="state = STATES.PRESCRIBER_SEARCH"
      />
    </template>
    <template
      v-else-if="state === STATES.PRESCRIBER_SEARCH"
      #actions
    >
      <BaseButton
        :text="$t('generic_action_continue')"
        type="submit"
        form="request-prescription-search"
        data-qa="continue"
        data-dd-action-name="Continue to personal details"
        @click="continueFromSearch"
      />
    </template>
    <template
      v-else-if="onPersonalStep"
      #actions
    >
      <BaseButton
        :loading="state === STATES.SUBMITTING"
        :text="$t('eu__doctor_outreach_consent__submit')"
        type="submit"
        form="request-prescription-personal"
        data-qa="submit"
        data-dd-action-name="Send outreach request"
        @click="sendRequest"
      />
    </template>
    <template
      v-else-if="state === STATES.UNAVAILABLE"
      #actions
    >
      <BaseButton
        :text="$t('eu__upload_prescription_link__unavailable__retry_cta')"
        data-qa="retry"
        data-dd-action-name="Retry link check"
        @click="checkLink"
      />
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'petite-vue-i18n'
import env from '@shared/env.js'
import { REQUEST_PRESCRIPTION_PAGE } from '@shared/actionLinks/pages.js'
import useForm from '@shared/composables/useForm.js'
import useUserStore from '@shared/stores/useUserStore.js'
import defineLanguage from '@shared/utils/defineLanguage.js'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import Base64Spinner from '@shared/components/Base64Spinner.vue'
import BaseBanner from '@shared/components/BaseBanner.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import BaseModal from '@shared/components/BaseModal.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import DocumentViewer from '@shared/components/DocumentViewer.vue'
import TextField from '@shared/components/form/TextField.vue'
import DateOfBirthField from '@shared/components/form/DateOfBirthField.vue'
import HeaderHelp from '@eu/app/components/HeaderHelp.vue'
import DoctorPracticePicker from '@eu/app/components/DoctorPracticePicker.vue'
import { takeAlias } from '@eu/app/actionLinks/captureAlias.js'
import {
  ERRORS,
  requestDoctorOutreach,
  searchPractices,
  validate,
} from '@eu/app/actionLinks/api.js'

const STATES = {
  CHECKING: 'checking',
  ADD_PRESCRIBER: 'add-prescriber',
  PRESCRIBER_SEARCH: 'prescriber-search',
  PERSONAL: 'personal',
  SUBMITTING: 'submitting',
  DONE: 'done',
  INVALID: 'invalid',
  UNAVAILABLE: 'unavailable',
}

const { t } = useI18n()
const userStore = useUserStore()

// The alias is the link's credential and stays while the page is open, for "try again", the
// searches and the request, until the page ends on invalid or done. Like the entries and the
// search results, it never goes into a store, storage or the URL.
let alias = takeAlias()

const state = ref(STATES.CHECKING)
const onPersonalStep = computed(
  () => state.value === STATES.PERSONAL || state.value === STATES.SUBMITTING,
)

const practiceQuery = ref('')
const pickedPractice = ref(null)
const confirmedPracticeId = ref(null)
const pickerRef = ref(null)
const manualEntry = ref({ prescriberName: '', cityOrZip: '' })
// Set on continue from the screen the user is on, so the request never names both practices
const chosenPractice = ref(null)

const personalDetails = ref({ firstName: '', lastName: '', dateOfBirth: '' })
const requestErrorModalRef = ref(null)

// The personal details go back to the screen their practice came from
const backTarget = computed(() => {
  if (state.value === STATES.PRESCRIBER_SEARCH) return STATES.ADD_PRESCRIBER
  if (!onPersonalStep.value) return null
  return 'doctor_practice_id' in chosenPractice.value
    ? STATES.PRESCRIBER_SEARCH
    : STATES.ADD_PRESCRIBER
})

const goBack = () => {
  state.value = backTarget.value
}

const consentText = computed(() =>
  t('eu__doctor_outreach_consent__description', {
    privacyLink: `${env.VITE_PUBLIC_PATH}static/${defineLanguage(userStore.lang)}/privacy-website/index.html`,
  }),
)

const endOnInvalidLink = () => {
  alias = null
  state.value = STATES.INVALID
}

const checkLink = async () => {
  if (alias == null) {
    state.value = STATES.INVALID
    return
  }

  state.value = STATES.CHECKING
  try {
    await validate(alias, REQUEST_PRESCRIPTION_PAGE.purpose)
    state.value = STATES.ADD_PRESCRIBER
  } catch (error) {
    if (error.kind === ERRORS.INVALID) {
      endOnInvalidLink()
    } else {
      state.value = STATES.UNAVAILABLE
    }
  }
}

// A link that is no longer valid ends the page instead of opening the search error dialog. Every
// other failure goes back to SearchSelectField, which ignores a canceled search and offers to try
// again for the rest. Nothing here keeps or logs the error: a cancellation's request holds the
// alias.
const findPractices = async (query, signal) => {
  try {
    return await searchPractices(alias, query, signal)
  } catch (error) {
    if (error.kind !== ERRORS.INVALID) throw error
    endOnInvalidLink()
    return []
  }
}

// The nearest practices are only a head start, and SearchSelectField would log their failure
const findNearestPractices = () => findPractices('').catch(() => [])

const continueWith = (practice) => {
  chosenPractice.value = practice
  state.value = STATES.PERSONAL
}

const continueFromSearch = () => {
  const practice = pickerRef.value.requirePractice()
  if (practice) continueWith({ doctor_practice_id: practice.id })
}

// A German postcode goes to the backend as zip and anything else as city, as in the app
const typedPractice = () => {
  const name = manualEntry.value.prescriberName.trim()
  const place = manualEntry.value.cityOrZip.trim()
  return /^\d{5}$/.test(place) ? { name, zip: place } : { name, city: place }
}

const { fieldAttrs: manualEntryAttrs, submit: submitManualEntry } = useForm(
  manualEntry,
  ref({
    prescriberName: { validations: ['required'] },
    cityOrZip: { validations: ['required'] },
  }),
  {
    onSubmit: () => continueWith({ doctor_practice_request: typedPractice() }),
    onSuccess: () => {},
    onError: () => {},
  },
)

const { fieldAttrs: personalDetailsAttrs, submit: submitPersonalDetails } =
  useForm(
    personalDetails,
    ref({
      firstName: { validations: ['required'] },
      lastName: { validations: ['required'] },
      dateOfBirth: { validations: ['dob'] },
    }),
    {
      onSubmit: async () => {
        state.value = STATES.SUBMITTING
        const { firstName, lastName, dateOfBirth } = personalDetails.value
        await requestDoctorOutreach(alias, {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          date_of_birth: dateOfBirth,
          ...chosenPractice.value,
        })
        alias = null
        state.value = STATES.DONE
      },
      onSuccess: () => {},
      onError: (error) => {
        if (error.kind === ERRORS.INVALID) {
          endOnInvalidLink()
          return
        }
        state.value = STATES.PERSONAL
        requestErrorModalRef.value?.open()
      },
    },
  )

const sendRequest = () => {
  if (state.value === STATES.SUBMITTING) return
  submitPersonalDetails()
}

const closeRequestError = () => {
  requestErrorModalRef.value?.close()
}

const retryRequest = () => {
  requestErrorModalRef.value?.close()
  sendRequest()
}

onMounted(checkLink)
</script>

<style lang="scss" scoped>
.request-prescription-link__spinner {
  width: 54px;
  height: 54px;
  margin: 32px auto;
}

.request-prescription-link__modal-actions {
  @include flex-container(row, flex-end, sm);
}
</style>
